import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { useUpdatePaymentStatus } from "features/consultations/hooks/useUpdatePaymentStatus";

const usePaymentActions = (patientId) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const queryClient = useQueryClient();
  const updatePaymentStatusMutation = useUpdatePaymentStatus();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["paymentsDue"] });
    queryClient.invalidateQueries({ queryKey: ["patientPayments"] });
    queryClient.invalidateQueries({ queryKey: ["familyPayments"] });
  };

  const handlePaymentConfirmation = async (payment) => {
    try {
      await updatePaymentStatusMutation.mutateAsync({
        paymentId: payment.id,
        status: "PAID",
      });
      toast.success("Payment status updated successfully");
      invalidateAll();
    } catch {
      toast.error("Failed to update payment status");
    }
  };

  const handleDownloadReceipt = async (payment) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/consultationpayment/receipt/${payment.id}/patient/${patientId}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `receipt-${payment.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Receipt downloaded successfully");
    } catch {
      toast.error("Failed to download receipt");
    }
  };

  const handlePrintStatement = async (ids) => {
    if (!ids?.length) return;
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/consultationpayment/receipt/getbyids`,
        ids,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `payment-receipts-${new Date().toISOString().split("T")[0]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded successfully");
    } catch {
      toast.error("Failed to generate receipt. Please try again.");
    }
  };

  return {
    handlePaymentConfirmation,
    handleDownloadReceipt,
    handlePrintStatement,
  };
};

export { usePaymentActions };
